import type {
    NotificationBodyDto,
    NotificationDto,
    NotificationsPageDto,
    UserNotificationsPageDto,
} from './dto';
import type {
    AppNotification,
    NotificationForm,
    NotificationsPage,
    UserNotifications,
} from './types';
import { stamp, statusOf } from './window';

function str(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === 'None') return null;
    return trimmed;
}

function toDate(value: string | null | undefined): Date | null {
    const raw = str(value);
    if (raw === null) return null;
    const parsed = new Date(raw.replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDateTime(date: string | null, time: string | null): Date | null {
    const day = str(date);
    if (day === null) return null;
    const clock = str(time) ?? '00:00:00';

    const iso = new Date(`${day}T${clock}`);
    if (!Number.isNaN(iso.getTime())) return iso;

    const spaced = new Date(`${day} ${clock}`);
    return Number.isNaN(spaced.getTime()) ? null : spaced;
}

export function toNotification(dto: NotificationDto): AppNotification {
    const startsAt = stamp(dto.start_date, dto.start_time);
    const endsAt = stamp(dto.end_date, dto.end_time);

    return {
        id: dto.id,
        text: str(dto.text) ?? '',
        author: str(dto.author) ?? '—',

        startsAt,
        endsAt,
        createdAtStamp: (str(dto.created_at) ?? '').slice(0, 16),

        createdAt: toDate(dto.created_at),
        startAt: toDateTime(dto.start_date, dto.start_time),
        endAt: toDateTime(dto.end_date, dto.end_time),

        status: statusOf({ startsAt, endsAt }),

        viewedGlobal: Boolean(dto.viewed),

        raw: {
            startDate: str(dto.start_date) ?? '',
            startTime: str(dto.start_time) ?? '',
            endDate: str(dto.end_date) ?? '',
            endTime: str(dto.end_time) ?? '',
        },
    };
}

export function toNotificationsPage(dto: NotificationsPageDto): NotificationsPage {
    return {
        items: Array.isArray(dto?.data) ? dto.data.map(toNotification) : [],
        total: dto?.count ?? 0,
        pages: dto?.pages ?? 1,
        pageNum: dto?.page_num ?? 1,
    };
}

export function toNotificationBody(form: NotificationForm): NotificationBodyDto {
    const immediate = Boolean(form.showNow);

    return {
        text: form.text.trim(),
        start_date: immediate ? '' : form.startDate.trim(),
        start_time: immediate ? '' : form.startTime.trim(),
        end_date: form.endDate.trim(),
        end_time: form.endTime.trim(),
    };
}

export function toUserNotifications(dto: UserNotificationsPageDto): UserNotifications {
    return {
        items: Array.isArray(dto?.data) ? dto.data.map(toNotification) : [],
        total: dto?.count ?? 0,
        pages: dto?.pages ?? 1,
        pageNum: dto?.page_num ?? 1,
    };
}